// MUST be the very first import: installs the in-memory storage shim for the
// Tech App Preview iframe BEFORE any other module can touch local/sessionStorage
// (a same-origin iframe shares storage with the admin tab — see techPreview.js).
import { isTechPreview, bootstrapPreviewSession } from './preview/techPreview'
import './security/cloudProductionGuard'
import axios from 'axios'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import i18n from './plugins/i18n'
import { ActivityTrackerPlugin } from './composables/useActivityTracker'
import { DateFieldPickerPlugin } from './plugins/dateFieldPicker'
import { initNetworkHealth } from './composables/useNetworkHealth'
import { initSyncEngine } from './services/syncEngine'
import { initErrorLogger, vueErrorHandler, axiosErrorInterceptor, logError } from './services/errorLogger'
import {
  decideStaleChunkReload,
  decideVersionReload,
  installReloadGuardListeners,
  publishVersionUpdate,
  reloadBlockReason,
} from './services/versionReloadGuard'
import apiClient, { clearAuthStorage } from './api/client'
import { getAccessToken, getRefreshToken, refreshAccessToken, lastRefreshUsedCookie, purgeLegacyStoredTokens, beginBootGate, loadStoredTokens, clearAccessToken, clearRefreshToken } from './auth/tokenManager'
import { nativeAuthContextState } from './auth/nativeSessionState'
import { purgeSecureCredentialsOnFirstLaunch } from './auth/nativeInstallGuard'
import { isNative } from '@/native/platform'
import { secureRemove, secureClearAllNative } from '@/native/secureStorage'
import { applyHostBrandFavicon } from '@/config/hostBrands'
import { initNativeShell } from '@/native/shell'
import { hideNativeSplash, paintNativeBootBackground } from '@/native/splashScreen'
import { initCapgoOta, notifyCapgoAppReady, refreshCapgoOtaConfig } from '@/native/capgoOta'
import './style.css'
import './styles/premium-skin.css'
import './styles/date-fields.css'
import 'flag-icons/css/flag-icons.min.css'

function safeLocalStorageGet(key) {
  try { return localStorage.getItem(key) } catch { return null }
}

function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

function safeLocalStorageRemove(key) {
  try { localStorage.removeItem(key) } catch { /* storage unavailable */ }
}

installReloadGuardListeners()

;(async () => {
  // Native: paint navy before any async boot work so the WebView never shows
  // the OS dark-mode black canvas while JS bundles parse.
  paintNativeBootBackground()

  // A tenant's own domain must not show the platform's tab icon (see
  // config/hostBrands.js). Done before anything else so the "5" star is gone on
  // the first frame the user sees.
  applyHostBrandFavicon()

  // 0. Native only: the Keychain outlives an uninstall while the WebView cache
  //    does not, so the first launch of a new install starts by discarding the
  //    previous installation's credentials — BEFORE loadStoredTokens() can mirror
  //    a refresh token that has no user, company or rights to go with it.
  const installPurge = await purgeSecureCredentialsOnFirstLaunch({
    native: isNative(),
    storage: localStorage,
    removeSecureKey: secureRemove,
    clearAllSecureKeys: secureClearAllNative,
  })
  if (installPurge.purged) {
    console.warn('[main] First launch after install — discarded secure credentials:',
      installPurge.removed.join(', ') || 'none',
      installPurge.clearedAll ? '(Keychain clear-all)' : '')
    if (!installPurge.markerPersisted) {
      console.warn('[main] Install marker could not be stored — the purge will repeat next launch')
    }
  } else if (installPurge.reason === 'storage-unavailable') {
    console.warn('[main] Install marker unreadable — keeping secure credential')
  }

  // 0b. Native only: warm the in-memory refresh-token mirror from secure storage
  //    so the body-refresh path works on the very first 401 (no-op on web).
  await loadStoredTokens()
  const nativeContextState = isNative() && (getAccessToken() || getRefreshToken())
    ? nativeAuthContextState(localStorage)
    : null
  // iOS Keychain survives uninstall/reinstall while WKWebView localStorage does
  // not. That can leave a refresh token without its user/domain identity.
  // Treat this as an orphaned session before the router mounts;
  // otherwise silent refresh authenticates an empty office shell and the
  // dashboard/profile guards redirect forever.
  if (nativeContextState === 'orphaned') {
    clearAccessToken()
    clearRefreshToken()
    await secureClearAllNative()
    clearAuthStorage()
    safeLocalStorageRemove('features')
    console.warn('[main] Cleared orphaned native session with incomplete auth context')
  } else if (nativeContextState === 'repairable') {
    // A valid cached identity with only missing/corrupt rights can be repaired
    // after silent refresh. Remove the bad value now so loadFromStorage() cannot
    // throw while parsing it.
    safeLocalStorageRemove('permissions')
    console.warn('[main] Native session has repairable permission context')
  } else if (nativeContextState === 'unavailable') {
    console.warn('[main] Native auth context storage unavailable — keeping secure credential')
  }
  // Drop zombie native profiles BEFORE App.vue loadFromStorage() — unless biometric
  // lock is enabled (keep cached profile + refresh for fingerprint unlock).
  // A profile is a zombie only when NO credential can revive it. On native the
  // access token lives in memory only, so after a kill+relaunch it is always
  // empty — a refresh token in secure storage still means a live session, and
  // wiping the profile there logged technicians into the office shell
  // (is_technician_session gone, nothing restores it after the silent refresh).
  // A refresh that is genuinely rejected clears the same keys in
  // bootstrapSession()'s 400/401/403 branch.
  if (isNative() && !getAccessToken() && !getRefreshToken() && safeLocalStorageGet('user')) {
    let keepForBiometric = false
    try {
      const { shouldPreserveRefreshForBiometric } = await import('@/native/biometricLoginStatus')
      keepForBiometric = await shouldPreserveRefreshForBiometric()
      if (keepForBiometric) {
        const { ensureRefreshTokenForBiometricUnlock } = await import('@/native/biometric')
        await ensureRefreshTokenForBiometricUnlock()
      }
    } catch { /* ignore */ }
    if (keepForBiometric) {
      safeLocalStorageSet('is_technician_session', 'true')
      console.log('[main] Keeping profile for biometric unlock')
    } else {
      for (const k of [
        'user', 'domain', 'permissions', 'features',
        'is_technician_session', 'interface_version_id',
      ]) {
        try { localStorage.removeItem(k) } catch (_) { /* ignore */ }
      }
      console.log('[main] Cleared zombie native session before app mount')
    }
  }
  // The native shell is the technician app — LoginView forces technician mode
  // there and hides the office toggle, so a live session on it is always a
  // technician session. When the profile survived but is_technician_session did
  // not, the router stopped redirecting to /technician and rendered the office
  // shell instead: a menu filtered down to nothing (technician rights match no
  // office group) over a page every guard bounced back. Restored BEFORE mount so
  // the router's first resolution already routes to the technician app.
  if (isNative() && (getAccessToken() || getRefreshToken())
      && safeLocalStorageGet('user')
      && safeLocalStorageGet('is_technician_session') !== 'true') {
    safeLocalStorageSet('is_technician_session', 'true')
    console.warn('[main] Restored is_technician_session for a live native session')
  }

  // 1. Initialize error logger FIRST — captures errors from all subsequent init steps
  initErrorLogger(apiClient, () => {
    try {
      const raw = safeLocalStorageGet('user')
      if (raw) {
        const u = JSON.parse(raw)
        return { user_id: u.id, user_email: u.email, domain_id: u.current_domain_id }
      }
    } catch { /* ignore */ }
    return { user_id: null, user_email: '', domain_id: null }
  })

  // 2. Install axios error interceptor (logs ALL api errors to DB)
  apiClient.interceptors.response.use(null, axiosErrorInterceptor)

  // 3–4. Network health + sync engine.
  // Web/PWA: run BEFORE mount so offline state is known on first paint.
  // Native: mount first — initNetworkHealth awaits the first health ping (up
  // to 5s) and blocks the WebView main thread, which triggers ANR on emulators
  // and low-end devices. Login/API still work; health ping catches up in bg.
  const bootNetworkServices = async () => {
    await initNetworkHealth()
    // Preview iframe: never start the offline sync engine — it would replay
    // queued mutations from the shared IndexedDB under the preview identity.
    if (!isTechPreview()) {
      await initSyncEngine(apiClient)
    }
  }
  if (!isNative()) {
    await bootNetworkServices()
  }

  const app = createApp(App)

  // 5. Vue error handler — catches component render/lifecycle errors + auto-reload on chunk failure
  app.config.errorHandler = (err, instance, info) => {
    // Auto-reload on stale chunk errors (after deploy, old cached JS/CSS is gone)
    if (reloadOnStaleChunk(err?.message || '')) return
    vueErrorHandler(err, instance, info)
  }

  // Stale-asset failures after a deploy also surface OUTSIDE Vue's render
  // pipeline: Vite emits `vite:preloadError` for failed dynamic-import / CSS
  // preloads, and bare promise rejections (e.g. "Unable to preload CSS for…")
  // arrive as unhandledrejection. Recover the same way so a user mid-session
  // on the old build is moved onto the fresh one instead of seeing a crash.
  window.addEventListener('vite:preloadError', (event) => {
    reloadOnStaleChunk(event?.payload?.message || 'vite:preloadError')
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason
    reloadOnStaleChunk((reason && (reason.message || String(reason))) || '')
  })

  app.use(createPinia())
  app.use(router)
  app.use(vuetify)
  app.use(i18n)
  app.use(ActivityTrackerPlugin)
  app.use(DateFieldPickerPlugin)

  // 5.5 Auth bootstrap — kick off a silent refresh so the in-memory access token
  // is fresh on every load (and, in dual/strict, populated from the httpOnly
  // cookie). The first API requests WAIT for this via the boot gate, so a reload
  // after the 15-min access cookie expired no longer flashes transient 401s. The
  // race() safety cap guarantees the gate settles (≤4s) even if refresh stalls,
  // so requests can never hang on boot — they fall back to the 401→refresh path.
  if (isTechPreview()) {
    // Preview iframe: the ONLY credential is the preview token from the URL
    // hash. No silent refresh (it would ride the admin's cookie). Load the
    // technician context and seed the shimmed storage BEFORE mount so the
    // router guard and auth store see a normal technician session.
    try {
      await bootstrapPreviewSession()
    } catch (e) {
      console.error('[main] Tech preview bootstrap failed:', e?.message || e)
    }
  } else {
    beginBootGate(Promise.race([
      bootstrapSession(),
      new Promise(resolve => setTimeout(resolve, 4000)),
    ]))
  }

  app.mount('#app')

  if (isNative()) {
    window.__fsjAppMounted = true
    hideNativeSplash()
    initNativeShell().catch((e) => {
      console.warn('[main] native shell init failed:', e?.message || e)
    })
    initCapgoOta()
      .then(() => notifyCapgoAppReady())
      .catch((e) => {
        console.warn('[main] Capgo init/notifyAppReady failed:', e?.message || e)
      })
    bootNetworkServices().catch((e) => {
      console.warn('[main] native network/sync boot failed:', e?.message || e)
    })
  }

  // 6. Branding bootstrap — apply cached vars immediately (already done by
  //    inline script in index.html), then refresh from API in background.
  try {
    const { useBrandingStore } = await import('./stores/branding')
    const branding = useBrandingStore()
    branding.setVuetify(vuetify)  // enable switch-triggered theme regeneration
    branding.applyToDom(vuetify)  // re-apply with full Vuetify integration
    // Authenticated user: pull from /current/. Otherwise: skip (LoginView handles /public/).
    // A cached user profile means a prior authenticated session whose auth now
    // rides the httpOnly cookie (token no longer in JS storage). Treat that as
    // "has access" so branding/profile refresh still runs after a reload.
    const hasAccess = !!(getAccessToken() || getRefreshToken() || (!isNative() && safeLocalStorageGet('user')))
    if (hasAccess) {
      // Pull the latest profile (ui_preferences) from the server FIRST so the
      // look matches the ACCOUNT in the DB on every device, not the per-browser
      // cache. Best-effort: on failure we keep the cached look (no blank screen).
      try {
        const { useAuthStore } = await import('./stores/auth')
        await useAuthStore().refreshUser()
      } catch (e) { console.warn('[main] refreshUser on boot failed (using cache):', e?.message) }

      branding.loadCurrent(vuetify)
        .catch(() => {/* keep cached */})
        .finally(async () => {
          // Apply the account's icon set / button skin / personal theme from the
          // freshly refreshed prefs (DB is the source of truth, not localStorage).
          try { branding.syncAccountPrefs(vuetify) } catch (e) { console.warn('[main] syncAccountPrefs failed:', e?.message) }
          if (isNative()) {
            try { await refreshCapgoOtaConfig() } catch (e) { console.warn('[main] Capgo OTA refresh failed:', e?.message) }
          }
          // Custom Design: apply the user's active custom theme ON TOP of branding.
          try {
            const { useCustomThemesStore } = await import('./stores/customThemes')
            const custom = useCustomThemesStore()
            await custom.load()
            custom.applyActive(branding.effectiveMode)
          } catch (e) { console.warn('[main] Custom theme bootstrap failed:', e?.message) }
        })
    }
  } catch (e) {
    console.warn('[main] Branding bootstrap failed:', e?.message)
  }

  // 7. Background version check — web/PWA only (native has no service worker).
  // Skipped in the preview iframe: an auto-reload there would drop the
  // preview token (it lives only in memory).
  if (!isNative() && !isTechPreview()) {
    _checkVersionPeriodically()
  }
})()

// Auth bootstrap: obtain a fresh in-memory access token at startup via a silent
// refresh. Best-effort and non-destructive — a failure NEVER logs the user out
// (the cached/mirrored token keeps the session alive). Skipped for God Mode tabs
// (own session token, no refresh) and for anonymous visitors (no refresh token).
async function bootstrapSession() {
  try {
    // God Mode tabs carry their own sessionStorage token (no cookie/refresh).
    if (sessionStorage.getItem('god_mode')) return
    const hasCred = !!(getRefreshToken() || getAccessToken())
    const hasCachedUser = !!safeLocalStorageGet('user')
    // Native: a cached profile without tokens is a zombie — unless biometric lock
    // keeps refresh + profile for fingerprint unlock.
    if (isNative() && hasCachedUser && !hasCred) {
      let keepForBiometric = false
      try {
        const { shouldPreserveRefreshForBiometric } = await import('@/native/biometricLoginStatus')
        keepForBiometric = await shouldPreserveRefreshForBiometric()
        if (keepForBiometric) {
          const { ensureRefreshTokenForBiometricUnlock } = await import('@/native/biometric')
          await ensureRefreshTokenForBiometricUnlock()
        }
      } catch { /* ignore */ }
      if (keepForBiometric && getRefreshToken()) {
        safeLocalStorageSet('is_technician_session', 'true')
        console.log('[main] bootstrapSession — keeping profile for biometric unlock')
        return
      }
      safeLocalStorageRemove('user')
      safeLocalStorageRemove('domain')
      safeLocalStorageRemove('permissions')
      safeLocalStorageRemove('features')
      safeLocalStorageRemove('is_technician_session')
      safeLocalStorageRemove('interface_version_id')
      return
    }
    const hadSession = hasCred || (!isNative() && hasCachedUser)
    if (!hadSession) return
    const token = await refreshAccessToken(axios)
    // Migration: once the backend has set the httpOnly cookie (dual/strict), drop
    // any JS-readable tokens from localStorage. Gated on cookie_auth so legacy
    // domains (no cookie) keep their body token and never get locked out.
    if (token && lastRefreshUsedCookie()) {
      purgeLegacyStoredTokens()
    }
  } catch (e) {
    const st = e?.response?.status
    // Native: a dead refresh token in Keychain must be removed or every login
    // attempt races entitlements/dialer/orch 401 → refresh 400 → logout.
    if (isNative() && (st === 400 || st === 401 || st === 403)) {
      let keepForBiometric = false
      try {
        const { shouldPreserveRefreshForBiometric } = await import('@/native/biometricLoginStatus')
        keepForBiometric = await shouldPreserveRefreshForBiometric()
        if (keepForBiometric) {
          const { ensureRefreshTokenForBiometricUnlock } = await import('@/native/biometric')
          await ensureRefreshTokenForBiometricUnlock()
        }
      } catch { /* ignore */ }
      if (!keepForBiometric) {
        clearRefreshToken()
        for (const k of [
          'user', 'domain', 'permissions', 'features',
          'is_technician_session', 'interface_version_id',
        ]) {
          try { localStorage.removeItem(k) } catch (_) { /* ignore */ }
        }
        console.warn('[main] boot refresh rejected (', st, ') — cleared stale native session')
      } else {
        console.warn('[main] boot refresh rejected (', st, ') — keeping profile for biometric unlock')
        safeLocalStorageSet('is_technician_session', 'true')
      }
    } else {
      console.warn('[main] boot silent refresh failed (keeping cached token):', e?.message)
    }
  }
}

// Centralised stale-asset recovery. After a deploy, a tab still running the old
// build requests chunk/CSS files whose hashed names no longer exist; the server
// returns index.html (text/html) and the import/preload fails. We reload once to
// land on the fresh build. Guarded by a per-tab circuit breaker (max 2 reloads
// in a 60s window) so a genuinely broken asset can never cause a reload loop.
let staleRetryTimer = null

function scheduleStaleRetry(msg) {
  if (staleRetryTimer) return
  staleRetryTimer = setTimeout(() => {
    staleRetryTimer = null
    reloadOnStaleChunk(msg)
  }, 15000)
}

function reloadOnStaleChunk(msg) {
  const key = 'vue_chunk_reload'
  const now = Date.now()
  let count = 0
  let lastAt = 0
  try {
    count = parseInt(sessionStorage.getItem(key + '_count') || '0', 10)
    lastAt = parseInt(sessionStorage.getItem(key) || '0', 10)
  } catch { /* private mode */ }

  const decision = decideStaleChunkReload({
    message: msg,
    blockedReason: reloadBlockReason(),
    count,
    lastAt,
    now,
  })
  if (decision.action === 'ignore') return false
  if (decision.action === 'postpone') {
    publishVersionUpdate({
      pending: true,
      blockedReason: decision.reason,
      kind: 'stale-chunk',
      banner_shown: true,
    })
    console.warn('[App] Stale chunk postponed (' + decision.reason + '):', msg)
    scheduleStaleRetry(msg)
    return true
  }
  if (decision.action !== 'reload') {
    if (count >= 2) {
      console.error('[App] Stale chunk: max reloads reached, not reloading again:', msg)
    }
    return false
  }
  try {
    sessionStorage.setItem(key, String(now))
    sessionStorage.setItem(key + '_count', String(count + 1))
  } catch { /* ignore */ }
  console.warn('[App] Stale chunk/CSS detected, reloading (attempt', count + 1, '):', msg)
  window.location.reload()
  return true
}

async function _checkVersionPeriodically() {
  const CHECK_INTERVAL = 2 * 60 * 1000
  const localVersion = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? String(__BUILD_TIMESTAMP__) : ''
  if (!localVersion) return

  // Per-TAB guard (sessionStorage, NOT localStorage): every tab must update
  // itself independently. The old localStorage flag meant only the FIRST tab
  // reloaded after a deploy — all other tabs stayed on the stale build forever.
  const RELOADED_KEY = '_version_reloaded_for'
  const ATTEMPTS_KEY = '_version_reload_attempts'
  const HISTORY_KEY = '_version_reload_history'          // [{ts, from, to}] of auto-reloads in this tab
  const DISABLED_KEY = '_version_autoreload_disabled_until'
  const PENDING_LOG_KEY = '_version_reload_pending_log'  // reload record sent to DB after the reload lands
  const FLAP_LOGGED_KEY = '_version_flapping_logged'
  const MAX_ATTEMPTS = 2                 // per same version
  const LOOP_WINDOW_MS = 15 * 60 * 1000  // circuit breaker window
  const LOOP_MAX_RELOADS = 3             // max auto-reloads per window, ANY versions
  const DISABLE_MS = 60 * 60 * 1000      // breaker tripped → no auto-reloads for 1h
  let reloading = false
  let postponedByCall = false

  // Migration: clear the legacy shared flag so tabs still running the old
  // build get their one reload chance and pick up the new code.
  try { localStorage.removeItem('_version_reloaded_for') } catch { /* ignore */ }

  // Send the DB log for the reload that just brought us here (the record is
  // written to sessionStorage right before reload, so it survives it and is
  // guaranteed to be sent — sendBeacon before reload is unreliable).
  try {
    const pending = sessionStorage.getItem(PENDING_LOG_KEY)
    if (pending) {
      sessionStorage.removeItem(PENDING_LOG_KEY)
      const rec = JSON.parse(pending)
      logError('custom', `[VersionCheck] auto-reload ${rec.from} -> ${rec.to} (attempt ${rec.attempt})`, {
        component: 'VersionCheck',
        action: 'version_reload',
        extra: { ...rec, landed_on: localVersion, landed_ok: rec.to === localVersion },
      })
    }
  } catch { /* ignore */ }

  // Call, dirty technician form, open dialog, focused textarea, or a text
  // field edited in the last two minutes. The resume interval uses the same
  // helper, so a postponed reload waits until the text is no longer at risk.
  function reloadBlocked() {
    return !!reloadBlockReason()
  }

  function logReloadPostponed(serverVersion, reason) {
    try {
      const marker = `${serverVersion}:${reason}`
      const key = '_version_reload_postponed_logged'
      if (sessionStorage.getItem(key) === marker) return
      sessionStorage.setItem(key, marker)
      logError('custom', `[VersionCheck] reload postponed (${reason})`, {
        component: 'VersionCheck',
        action: 'version_reload_postponed',
        extra: {
          local: localVersion,
          server: serverVersion,
          blocked_reason: reason,
          banner_shown: true,
        },
      })
    } catch { /* ignore */ }
  }

  function reloadHistory() {
    try { return JSON.parse(sessionStorage.getItem(HISTORY_KEY) || '[]') } catch { return [] }
  }

  function autoReloadDisabled() {
    return parseInt(sessionStorage.getItem(DISABLED_KEY) || '0', 10) > Date.now()
  }

  async function fetchServerVersion() {
    const res = await fetch('/version.json?_=' + Date.now(), { cache: 'no-store' })
    if (!res.ok) return ''
    const data = await res.json()
    return String(data.timestamp || '')
  }

  // Returns true when a reload is guaranteed to serve the NEW build: either
  // the fresh service worker has taken control, or there is no SW at all (the
  // server then serves fresh files directly). Returns false while the new
  // worker is still downloading/installing the precache — reloading at that
  // point would just re-serve the stale precache and burn a reload attempt.
  async function freshSwReady() {
    if (!('serviceWorker' in navigator)) return true
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg) return true
      await reg.update().catch(() => {})
      // No install in flight → the new worker already activated (skipWaiting
      // + clientsClaim) or it activated between checks. Safe to reload.
      if (!reg.waiting && !reg.installing) return true
      return await new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(false), 25000)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          clearTimeout(timeout)
          resolve(true)
        }, { once: true })
      })
    } catch {
      return true // SW layer broken — plain reload still helps
    }
  }

  async function swReadyThenReload(serverVersion, attempt) {
    if (reloading) return
    reloading = true
    const ready = await freshSwReady()
    if (!ready) {
      // New build not downloaded yet (slow network / large precache). Do NOT
      // reload and do NOT burn the attempt — the next check cycle retries,
      // by which time the SW install has usually finished.
      reloading = false
      console.warn('[VersionCheck] New build not yet served by SW — reload postponed, will retry')
      try {
        const SW_WAIT_LOGGED_KEY = '_version_sw_wait_logged'
        if (sessionStorage.getItem(SW_WAIT_LOGGED_KEY) !== serverVersion) {
          sessionStorage.setItem(SW_WAIT_LOGGED_KEY, serverVersion)
          logError('custom', '[VersionCheck] SW not ready for new build — reload postponed', {
            component: 'VersionCheck',
            action: 'version_sw_not_ready',
            extra: { local: localVersion, server: serverVersion },
          })
        }
      } catch { /* ignore */ }
      return
    }
    console.warn('[VersionCheck] Reloading for new version:', localVersion, '->', serverVersion)
    try {
      const h = reloadHistory().filter(r => Date.now() - r.ts < LOOP_WINDOW_MS)
      h.push({ ts: Date.now(), from: localVersion, to: serverVersion })
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify(h))
      sessionStorage.setItem(PENDING_LOG_KEY, JSON.stringify({
        from: localVersion, to: serverVersion, attempt, at: new Date().toISOString(),
      }))
      sessionStorage.setItem(RELOADED_KEY, serverVersion)
      // Count the attempt only here, on an actual reload — postponed reloads
      // (SW not ready) must not eat into MAX_ATTEMPTS.
      sessionStorage.setItem(ATTEMPTS_KEY, String(attempt))
    } catch { /* ignore */ }
    window.location.reload()
  }

  async function check() {
    if (reloading || autoReloadDisabled()) return
    try {
      const serverVersion = await fetchServerVersion()
      if (!serverVersion || serverVersion === localVersion) {
        publishVersionUpdate({ pending: false })
        return
      }

      // Confirmation probe: the version must be reported twice in a row.
      // Cuts off network flukes and "ping-pong" (flapping) version sources
      // before any reload happens.
      await new Promise(r => setTimeout(r, 2000))
      const confirmVersion = await fetchServerVersion()

      // Per-version retry limit (SW kept serving the stale precache).
      // The attempt counter is persisted inside swReadyThenReload, right
      // before the actual reload — a postponed reload doesn't consume it.
      let attempt = 1
      try {
        if (sessionStorage.getItem(RELOADED_KEY) === serverVersion) {
          attempt = parseInt(sessionStorage.getItem(ATTEMPTS_KEY) || '1', 10) + 1
        }
      } catch { /* ignore */ }

      const recent = reloadHistory().filter(r => Date.now() - r.ts < LOOP_WINDOW_MS)
      const decision = decideVersionReload({
        localVersion,
        serverVersion,
        confirmVersion,
        blockedReason: reloadBlockReason(),
        attempt,
        maxAttempts: MAX_ATTEMPTS,
        recentReloads: recent.length,
        maxReloads: LOOP_MAX_RELOADS,
      })

      if (decision.action === 'skip' && decision.why === 'flapping') {
        console.warn('[VersionCheck] Version flapping, reload skipped:', serverVersion, 'vs', confirmVersion)
        if (!sessionStorage.getItem(FLAP_LOGGED_KEY)) {
          sessionStorage.setItem(FLAP_LOGGED_KEY, '1')
          logError('custom', '[VersionCheck] version.json flapping detected — reload skipped', {
            component: 'VersionCheck',
            action: 'version_flapping',
            extra: { first: serverVersion, second: confirmVersion, local: localVersion },
          })
        }
        return
      }
      if (decision.action === 'breaker') {
        sessionStorage.setItem(DISABLED_KEY, String(Date.now() + DISABLE_MS))
        console.error('[VersionCheck] Reload loop detected — auto-update disabled for 1h')
        logError('custom', '[VersionCheck] reload loop stopped by circuit breaker (auto-update off for 1h)', {
          component: 'VersionCheck',
          action: 'version_reload_loop_stopped',
          extra: { recent_reloads: recent, local: localVersion, server: serverVersion },
        })
        return
      }
      if (decision.action === 'postpone') {
        postponedByCall = true
        publishVersionUpdate({
          pending: true,
          serverVersion,
          blockedReason: decision.reason,
          banner_shown: true,
        })
        console.warn('[VersionCheck] New version available, reload postponed (' + decision.reason + ')')
        logReloadPostponed(serverVersion, decision.reason)
        return
      }
      if (decision.action !== 'reload') return
      publishVersionUpdate({ pending: false })
      await swReadyThenReload(serverVersion, attempt)
    } catch { /* offline or server error — skip */ }
  }

  // Mobile PWA / technicians: re-check the moment the app becomes visible
  // again or the network comes back, instead of waiting for the next tick.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })
  window.addEventListener('online', () => check())

  // A reload postponed because of an active call or an in-progress form
  // re-runs the FULL check (incl. confirmation probe and circuit breaker) once
  // the call ends / the form is saved & closed.
  setInterval(() => {
    if (postponedByCall && !reloadBlocked()) {
      postponedByCall = false
      check()
    }
  }, 30000)

  setTimeout(check, 30000)
  setInterval(check, CHECK_INTERVAL)
}
